/**
 * Area Types and Groupings
 *
 * Groups area_type enum values for navigation and filtering.
 * Uses AreaType from hangars.ts (which maps to database area_type enum).
 */

import type { AreaType } from './hangars'
import { AREA_TYPE_LABELS } from './hangars'

/** Re-export for convenience */
export type { AreaType }
export { AREA_TYPE_LABELS }

// ============================================
// Structural Type Groupings
// ============================================

/** Enclosed hangar types (single or multi-unit buildings with doors) */
export const ENCLOSED_HANGAR_TYPES = [
  'single_unit_hangar',
  'box_hangar_single_row',
  'box_hangar_double_row',
  'arch_hangar',
] as const

/** T-Hangar types (rows of T-shaped individual units) */
export const T_HANGAR_TYPES = ['t_hangar_row', 'nested_t_hangar_row'] as const

/** Outdoor area types (no walls, no doors) */
export const OUTDOOR_TYPES = ['ramp_area', 'tiedown_area', 'shade_structure'] as const

/** Non-aircraft building types */
export const NON_AIRCRAFT_TYPES = ['terminal', 'office'] as const

/** Event placement types (auto-placement infrastructure, non-allocatable) */
export const EVENT_PLACEMENT_TYPES = ['exit_point', 'fuel_depot'] as const

/** Custom types */
export const CUSTOM_TYPES = ['custom'] as const

/** All hangar types (enclosed buildings) */
export const ALL_HANGAR_TYPES = [
  ...ENCLOSED_HANGAR_TYPES,
  ...T_HANGAR_TYPES,
] as const

/** All outdoor types */
export const ALL_OUTDOOR_TYPES = [...OUTDOOR_TYPES] as const

// ============================================
// Area Group Configuration
// ============================================

export interface AreaGroup {
  id: string
  label: string
  description: string
  types: readonly AreaType[]
}

export const AREA_GROUPS: AreaGroup[] = [
  {
    id: 'enclosed-hangars',
    label: 'Enclosed Hangars',
    description: 'Single or multi-unit enclosed structures',
    types: ENCLOSED_HANGAR_TYPES,
  },
  {
    id: 't-hangars',
    label: 'T-Hangars',
    description: 'T-shaped individual hangar rows',
    types: T_HANGAR_TYPES,
  },
  {
    id: 'outdoor-areas',
    label: 'Outdoor Areas',
    description: 'Ramps, tiedowns, shade structures',
    types: OUTDOOR_TYPES,
  },
  {
    id: 'non-aircraft',
    label: 'Non-Aircraft',
    description: 'Terminals, offices, admin buildings',
    types: NON_AIRCRAFT_TYPES,
  },
  {
    id: 'event-placement',
    label: 'Event Placement',
    description: 'Runway exits, fuel depots, and auto-placement infrastructure',
    types: EVENT_PLACEMENT_TYPES,
  },
]

// ============================================
// Helper Functions
// ============================================

/** Check if type is a T-hangar variant */
export const isTHangarType = (type: AreaType): boolean =>
  (T_HANGAR_TYPES as readonly string[]).includes(type)

/** Check if type is an enclosed hangar */
export const isEnclosedHangarType = (type: AreaType): boolean =>
  (ENCLOSED_HANGAR_TYPES as readonly string[]).includes(type)

/** Check if type is any hangar type (enclosed building) */
export const isHangarType = (type: AreaType): boolean =>
  (ALL_HANGAR_TYPES as readonly string[]).includes(type)

/** Check if type is outdoor (no walls, no doors) */
export const isOutdoorAreaType = (type: AreaType): boolean =>
  (ALL_OUTDOOR_TYPES as readonly string[]).includes(type)

/** Check if type is non-aircraft (terminal, office) */
export const isNonAircraftType = (type: AreaType): boolean =>
  (NON_AIRCRAFT_TYPES as readonly string[]).includes(type)

/** Check if type is event-placement infrastructure (exit area) */
export const isEventPlacementType = (type: AreaType): boolean =>
  (EVENT_PLACEMENT_TYPES as readonly string[]).includes(type)

/** Check if type is a specialized non-standard area (placeholder for future types like maintenance) */
export const isSpecializedType = (_type: AreaType): boolean => false

/** Check if type requires door dimensions */
export const requiresDoors = (type: AreaType): boolean =>
  !isOutdoorAreaType(type) && type !== 'office' && type !== 'terminal'

/** Get area group for a type */
export const getAreaGroup = (type: AreaType): AreaGroup | undefined =>
  AREA_GROUPS.find((group) => (group.types as readonly string[]).includes(type))

/** Filter types by group */
export const getTypesForGroup = (groupId: string): AreaType[] => {
  const group = AREA_GROUPS.find((g) => g.id === groupId)
  return group ? [...group.types] : []
}

/** Parse comma-separated type filter from URL */
export const parseTypeFilter = (filter: string | null): AreaType[] => {
  if (!filter) return []
  return filter.split(',').filter((t): t is AreaType => {
    const allTypes = [
      ...ALL_HANGAR_TYPES,
      ...ALL_OUTDOOR_TYPES,
      ...NON_AIRCRAFT_TYPES,
      ...EVENT_PLACEMENT_TYPES,
      ...CUSTOM_TYPES,
    ]
    return (allTypes as readonly string[]).includes(t)
  })
}

/** Create type filter string for URL */
export const createTypeFilter = (types: AreaType[]): string => types.join(',')
